import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, X, Save, Calendar, User, FileText } from 'lucide-react';

const MainMenu = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); 
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    clientName: '',
    beginDate: '',
    endDate: '',
    image: null
  });

  // API Base URL - adjust this to match your backend URL
  const API_BASE_URL = 'https://alaussback-production.up.railway.app/api/projects';

  // Load projects from backend
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_BASE_URL);
      if (response.ok) {
        const data = await response.json();
        // Convert image bytes to base64 data URL for display
        const projectsWithImages = data.map(project => ({
          ...project,
          image: project.image ? `data:image/jpeg;base64,${project.image}` : null
        }));
        setProjects(projectsWithImages);
      } else {
        console.error('Failed to fetch projects:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      description: '',
      clientName: '',
      beginDate: '',
      endDate: '',
      image: null
    });
  };

  const openModal = (mode, project = null) => {
    setModalMode(mode);
    setSelectedProject(project);
    if (project) {
      setFormData({
        id: project.id,
        name: project.name,
        description: project.description,
        clientName: project.clientName,
        beginDate: project.beginDate,
        endDate: project.endDate,
        image: project.image
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProject(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        imageFile: file // Store the actual file for upload
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (modalMode === 'add') {
        await addProject();
      } else if (modalMode === 'edit') {
        await updateProject();
      }
      
      // Refresh projects list
      await fetchProjects();
      closeModal();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('حدث خطأ أثناء حفظ المشروع. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const addProject = async () => {
    const formDataToSend = new FormData();
    
    // Create project object without image
    const projectData = {
      name: formData.name,
      description: formData.description,
      clientName: formData.clientName,
      beginDate: formData.beginDate,
      endDate: formData.endDate
    };
    
    // Add project as JSON string
    formDataToSend.append('project', new Blob([JSON.stringify(projectData)], {
      type: 'application/json'
    }));
    
    // Add image file if exists
    if (formData.imageFile) {
      formDataToSend.append('image', formData.imageFile);
    }
    
    const response = await fetch(`${API_BASE_URL}/add`, {
      method: 'POST',
      body: formDataToSend
    });
    
    if (!response.ok) {
      throw new Error('Failed to add project');
    }
  };

  const updateProject = async () => {
    // Note: You'll need to add an update endpoint in your backend
    // For now, we'll use the add endpoint as a placeholder
    const formDataToSend = new FormData();
    
    const projectData = {
      id: formData.id,
      name: formData.name,
      description: formData.description,
      clientName: formData.clientName,
      beginDate: formData.beginDate,
      endDate: formData.endDate
    };
    
    formDataToSend.append('project', new Blob([JSON.stringify(projectData)], {
      type: 'application/json'
    }));
    
    if (formData.imageFile) {
      formDataToSend.append('image', formData.imageFile);
    }
    
    // This would need to be a PUT request to an update endpoint
    // For now, we'll simulate the update
    console.log('Update project - backend endpoint needed');
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) {
      setLoading(true);
      try {
        // Note: You'll need to add a delete endpoint in your backend
        const response = await fetch(`${API_BASE_URL}/${projectId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          await fetchProjects(); // Refresh the list
        } else {
          throw new Error('Failed to delete project');
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('حدث خطأ أثناء حذف المشروع. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ar-SA');
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      direction: 'rtl',
      fontFamily: 'Arial, sans-serif'
    },
    header: {
      backgroundColor: 'white',
      borderBottom: '1px solid #e5e5e5',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '16px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0
    },
    navContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '16px'
    },
    tabContainer: {
      display: 'flex',
      backgroundColor: '#e5e5e5',
      padding: '4px',
      borderRadius: '8px',
      width: 'fit-content'
    },
    tab: {
      padding: '8px 24px',
      borderRadius: '6px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: 'none',
      backgroundColor: 'transparent'
    },
    activeTab: {
      backgroundColor: 'white',
      color: '#2563eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    inactiveTab: {
      color: '#666',
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px 32px'
    },
    projectsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    projectsTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#333',
      margin: 0
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.2s',
      opacity: loading ? 0.5 : 1
    },
    projectsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '24px'
    },
    projectCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #e5e5e5',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'box-shadow 0.2s'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    cardContent: {
      flex: 1
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '8px'
    },
    cardClient: {
      color: '#666',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center'
    },
    cardDescription: {
      color: '#888',
      fontSize: '14px',
      lineHeight: '1.4',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    cardImage: {
      width: '64px',
      height: '64px',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '16px',
      overflow: 'hidden'
    },
    cardDate: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: '#888',
      marginBottom: '16px'
    },
    cardButtons: {
      display: 'flex',
      gap: '8px'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.2s',
      opacity: loading ? 0.5 : 1
    },
    viewButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    editButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px'
    },
    emptyStateTitle: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#333',
      marginBottom: '8px'
    },
    emptyStateDesc: {
      color: '#888',
      marginBottom: '16px'
    },
    loadingState: {
      textAlign: 'center',
      padding: '48px',
      color: '#666'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '24px',
      borderBottom: '1px solid #e5e5e5'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#888',
      padding: '4px'
    },
    modalBody: {
      padding: '24px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
      marginBottom: '4px'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      resize: 'vertical',
      minHeight: '100px',
      boxSizing: 'border-box'
    },
    dateGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px'
    },
    imagePreview: {
      marginTop: '8px'
    },
    previewImage: {
      width: '128px',
      height: '128px',
      objectFit: 'cover',
      borderRadius: '8px'
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      paddingTop: '16px'
    },
    saveButton: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      opacity: loading ? 0.5 : 1
    },
    cancelButton: {
      padding: '12px 24px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    viewField: {
      marginBottom: '16px'
    },
    viewValue: {
      color: '#333',
      marginTop: '4px'
    },
    icon: {
      marginLeft: '8px'
    }
  };

  const ProjectCard = ({ project }) => (
    <div style={{
      ...styles.projectCard,
      ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
    }}>
      <div style={styles.cardHeader}>
        <div style={styles.cardContent}>
          <h3 style={styles.cardTitle}>{project.name}</h3>
          <p style={styles.cardClient}>
            <User size={16} style={styles.icon} />
            {project.clientName}
          </p>
          <p style={styles.cardDescription}>{project.description}</p>
        </div>
        {project.image && (
          <div style={styles.cardImage}>
            <img 
              src={project.image} 
              alt={project.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
            />
          </div>
        )}
      </div>
      
      <div style={styles.cardDate}>
        <Calendar size={16} style={styles.icon} />
        <span>من {formatDate(project.beginDate)} إلى {formatDate(project.endDate)}</span>
      </div>
      
      <div style={styles.cardButtons}>
        <button
          onClick={() => openModal('view', project)}
          style={{...styles.button, ...styles.viewButton}}
          disabled={loading}
          onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          <Eye size={16} style={styles.icon} />
          عرض
        </button>
        <button
          onClick={() => openModal('edit', project)}
          style={{...styles.button, ...styles.editButton}}
          disabled={loading}
          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
        >
          <Edit2 size={16} style={styles.icon} />
          تعديل
        </button>
        <button
          onClick={() => handleDelete(project.id)}
          style={{...styles.button, ...styles.deleteButton}}
          disabled={loading}
          onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
        >
          <Trash2 size={16} style={styles.icon} />
          حذف
        </button>
      </div>
    </div>
  );

  const Modal = () => (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {modalMode === 'add' && 'إضافة مشروع جديد'}
            {modalMode === 'edit' && 'تعديل المشروع'}
            {modalMode === 'view' && 'تفاصيل المشروع'}
          </h2>
          <button onClick={closeModal} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>
        
        <div style={styles.modalBody}>
          {modalMode === 'view' ? (
            <div>
              <div style={styles.viewField}>
                <label style={styles.label}>اسم المشروع</label>
                <div style={styles.viewValue}>{selectedProject?.name}</div>
              </div>
              <div style={styles.viewField}>
                <label style={styles.label}>اسم العميل</label>
                <div style={styles.viewValue}>{selectedProject?.clientName}</div>
              </div>
              <div style={styles.viewField}>
                <label style={styles.label}>وصف المشروع</label>
                <div style={{...styles.viewValue, whiteSpace: 'pre-wrap'}}>{selectedProject?.description}</div>
              </div>
              <div style={styles.dateGrid}>
                <div style={styles.viewField}>
                  <label style={styles.label}>تاريخ البداية</label>
                  <div style={styles.viewValue}>{formatDate(selectedProject?.beginDate)}</div>
                </div>
                <div style={styles.viewField}>
                  <label style={styles.label}>تاريخ النهاية</label>
                  <div style={styles.viewValue}>{formatDate(selectedProject?.endDate)}</div>
                </div>
              </div>
              {selectedProject?.image && (
                <div style={styles.viewField}>
                  <label style={styles.label}>صورة المشروع</label>
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.name}
                    style={{width: '100%', maxWidth: '300px', height: '200px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px'}}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>اسم المشروع *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  placeholder="أدخل اسم المشروع"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>اسم العميل *</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  placeholder="أدخل اسم العميل"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>وصف المشروع</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  placeholder="أدخل وصف المشروع"
                />
              </div>
              
              <div style={styles.dateGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>تاريخ البداية *</label>
                  <input
                    type="date"
                    name="beginDate"
                    value={formData.beginDate}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>تاريخ النهاية</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>صورة المشروع</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={styles.input}
                />
                {formData.image && (
                  <div style={styles.imagePreview}>
                    <img 
                      src={formData.image} 
                      alt="معاينة"
                      style={styles.previewImage}
                    />
                  </div>
                )}
              </div>
              
              <div style={styles.modalButtons}>
                <button
                  onClick={handleSubmit}
                  style={styles.saveButton}
                  disabled={loading}
                >
                  <Save size={16} style={styles.icon} />
                  {loading ? 'جاري الحفظ...' : (modalMode === 'add' ? 'إضافة المشروع' : 'حفظ التعديلات')}
                </button>
                <button
                  onClick={closeModal}
                  style={styles.cancelButton}
                  disabled={loading}
                >
                  إلغاء
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>نظام إدارة المشاريع</h1>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={styles.navContainer}>
        <div style={styles.tabContainer}>
          <button
            onClick={() => setActiveTab('projects')}
            style={{
              ...styles.tab,
              ...(activeTab === 'projects' ? styles.activeTab : styles.inactiveTab)
            }}
          >
            المشاريع
          </button>
          <button
            onClick={() => setActiveTab('interface')}
            style={{
              ...styles.tab,
              ...(activeTab === 'interface' ? styles.activeTab : styles.inactiveTab)
            }}
          >
            الواجهة
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'projects' && (
          <div>
            {/* Projects Header */}
            <div style={styles.projectsHeader}>
              <h2 style={styles.projectsTitle}>
                إدارة المشاريع ({projects.length})
              </h2>
              <button
                onClick={() => openModal('add')}
                style={styles.addButton}
                disabled={loading}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <Plus size={16} style={styles.icon} />
                إضافة مشروع جديد
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div style={styles.loadingState}>
                <p>جاري التحميل...</p>
              </div>
            )}

            {/* Projects Grid */}
            {!loading && projects.length > 0 ? (
              <div style={styles.projectsGrid}>
                {projects.map(project => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            ) : !loading && (
              <div style={styles.emptyState}>
                <FileText size={64} style={{color: '#d1d5db', margin: '0 auto 16px'}} />
                <h3 style={styles.emptyStateTitle}>لا توجد مشاريع</h3>
                <p style={styles.emptyStateDesc}>ابدأ بإضافة مشروعك الأول</p>
                <button
                  onClick={() => openModal('add')}
                  style={{...styles.addButton, margin: '0 auto'}}
                  disabled={loading}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  <Plus size={16} style={styles.icon} />
                  إضافة مشروع جديد
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'interface' && (
          <div style={styles.emptyState}>
            <div style={{backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '32px'}}>
              <h2 style={{fontSize: '20px', fontWeight: '600', color: '#333', marginBottom: '16px'}}>قسم الواجهة</h2>
              <p style={{color: '#666'}}>سيتم تطوير هذا القسم لاحقاً</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && <Modal />}
    </div>
  );
};

export default MainMenu;